import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=') || 
        new URLSearchParams(window.location.search).get('code')) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, store_name, promo_code) => {
    const payload = { email, password, name, store_name };
    if (promo_code) payload.promo_code = promo_code;
    const response = await api.post('/auth/register', payload);
    setUser(response.data);
    return response.data;
  };

  const googleLogin = () => {
    const clientId = '81382852842-916c708l86d21lr6rakjb9povuta262p.apps.googleusercontent.com';
    const redirectUri = encodeURIComponent(window.location.origin + '/api/auth/google/callback');
    const scope = encodeURIComponent('openid email profile');
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=consent`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, logout, setUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
